import { LOG_DATE_FIELD_NAME } from '@monorepo/shared/componentsV2/logs/types';
import { useToast } from '@monorepo/shared/contexts';
import { IS_REQUIRED_ERROR } from '@monorepo/shared/utils/validators';
import React, { useCallback } from 'react';
import { useModal } from '../useModalV2';

function getBlockedSubmitMessage(
  entriesErrors: Record<string, unknown>[],
): string | undefined {
  const isSingleEntry = entriesErrors.length === 1;

  const isMissingLogDate = entriesErrors.some(
    (errors) => errors[LOG_DATE_FIELD_NAME] === IS_REQUIRED_ERROR,
  );
  if (isMissingLogDate) {
    return isSingleEntry
      ? 'Log date is required.'
      : 'Log date is required for all entries.';
  }

  const hasInvalidValue = entriesErrors.some((errors) =>
    Object.values(errors).some(
      (error) => !!error && error !== IS_REQUIRED_ERROR,
    ),
  );
  if (hasInvalidValue) {
    return isSingleEntry
      ? 'This entry has invalid values.'
      : 'Some entries have invalid values.';
  }

  return undefined;
}

type SubmitWithIncompleteChecksParams = {
  /** One field-id → error map per entry being saved. */
  entriesErrors: Record<string, unknown>[];
  submit: () => Promise<void>;
};

/**
 * Entries missing only soft-required values can be submitted after confirming.
 * A missing Log date blocks submit, as does any other validation error,
 * since invalid values save as empty.
 */
export function useConfirmIncompleteLogEntries() {
  const { confirm } = useModal();
  const { showUserFriendlyErrorToast } = useToast();

  const submitWithIncompleteChecks = useCallback(
    async ({ entriesErrors, submit }: SubmitWithIncompleteChecksParams) => {
      const blockedSubmitMessage = getBlockedSubmitMessage(entriesErrors);
      if (blockedSubmitMessage) {
        showUserFriendlyErrorToast(new Error(blockedSubmitMessage), undefined, {
          dontAutoHide: true,
        });
        return;
      }

      const entryCount = entriesErrors.length;
      const incompleteCount = entriesErrors.filter((errors) =>
        Object.values(errors).some(Boolean),
      ).length;
      if (incompleteCount === 0) {
        await submit();
        return;
      }

      await confirm({
        title:
          incompleteCount === 1
            ? 'Incomplete Log entry'
            : 'Incomplete Log entries',
        description: (
          <>
            You have not completed all required fields for{' '}
            {entryCount === 1
              ? 'this entry'
              : `${incompleteCount} of the ${entryCount} entries being saved`}
            . To comply with the requirements of this Log, you&apos;ll need to
            add a value to all required fields.
            <br />
            <br />
            Do you want to submit{' '}
            {incompleteCount === 1
              ? 'an incomplete entry'
              : `${incompleteCount} incomplete entries`}
            ?
          </>
        ),
        cancelButtonText: 'Return to entry form',
        confirmButtonText: 'Submit incomplete',
        afterConfirmButtonText: 'Submitting...',
        danger: true,
        onConfirmAsync: submit,
      });
    },
    [confirm, showUserFriendlyErrorToast],
  );

  return { submitWithIncompleteChecks };
}
